import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../utils/i18n';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Plus } from 'lucide-react';
import axios from 'axios';
import { ReferralWidget } from '../components/ReferralWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Dashboard = () => {
  const { user, locale, viewMode } = useAuth();
  const { t } = useTranslation(locale);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branding, setBranding] = useState({ logo: null });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch main data
      const [sessionsRes, vehiclesRes, subRes] = await Promise.all([
        axios.get(`${API}/sessions`),
        axios.get(`${API}/vehicles`),
        axios.get(`${API}/subscriptions/my-subscription`)
      ]);
      
      // Try to fetch branding (optional, may fail)
      let brandingRes = { data: { logo: null } };
      try {
        brandingRes = await axios.get(`${API}/admin/branding`);
      } catch (err) {
        console.log('Branding not available:', err.message);
      }
      
      setSessions(sessionsRes.data || []);
      setVehicles(vehiclesRes.data || []);
      setSubscription(subRes.data);
      setBranding(brandingRes.data || { logo: null });
      
      // If admin, fetch stats
      if (user?.is_admin) {
        const statsRes = await axios.get(`${API}/admin/stats`);
        setStats(statsRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const isAdminView = user?.is_admin && viewMode === 'admin';

  return (
    <div className="min-h-screen p-8 relative" style={{ backgroundColor: '#0B0F14' }}>
        {/* Logo Wasserzeichen */}
        {branding.logo && (
          <div 
            className="fixed top-0 right-0 w-1/3 h-full pointer-events-none flex items-center justify-center"
            style={{ 
              zIndex: 0,
              opacity: 0.03
            }}
          >
            <img 
              src={branding.logo} 
              alt="Logo Watermark"
              style={{
                maxWidth: '80%',
                maxHeight: '80%',
                objectFit: 'contain'
              }}
            />
          </div>
        )}
        <div className="max-w-7xl mx-auto relative" style={{ zIndex: 1 }}>
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-2">
              <h2 className="text-3xl font-bold" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
                {t('dashboard')}
              </h2>
              {/* Founding Member Badge */}
              {user?.is_founding_member && (
                <div 
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-yellow-500/20 to-amber-600/20 border border-yellow-500/50"
                  data-testid="founding-member-badge"
                >
                  <span className="text-lg">{user?.founding_member_badge?.split(' ')[0] || '🏆'}</span>
                  <span className="text-yellow-400 font-semibold text-sm">
                    Gründungsmitglied
                  </span>
                  {user?.lifetime_discount_percent > 0 && (
                    <span className="text-yellow-500 text-xs bg-yellow-500/20 px-2 py-0.5 rounded-full">
                      {user.lifetime_discount_percent}% Rabatt
                    </span>
                  )}
                </div>
              )}
            </div>
            <p style={{ color: '#94A3B8' }}>
              Willkommen zurück, {user?.email}
            </p>
          </div>

          {/* Admin Stats */}
          {isAdminView && stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
                <CardHeader>
                  <CardDescription style={{ color: '#94A3B8' }}>Total Users</CardDescription>
                  <CardTitle style={{ color: '#7CD4FD', fontSize: '2rem' }}>{stats.total_users}</CardTitle>
                </CardHeader>
              </Card>
              <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
                <CardHeader>
                  <CardDescription style={{ color: '#94A3B8' }}>Active Subscriptions</CardDescription>
                  <CardTitle style={{ color: '#22C55E', fontSize: '2rem' }}>{stats.active_subscriptions}</CardTitle>
                </CardHeader>
              </Card>
              <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
                <CardHeader>
                  <CardDescription style={{ color: '#94A3B8' }}>Total Sessions</CardDescription>
                  <CardTitle style={{ color: '#A78BFA', fontSize: '2rem' }}>{stats.total_sessions}</CardTitle>
                </CardHeader>
              </Card>
              <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
                <CardHeader>
                  <CardDescription style={{ color: '#94A3B8' }}>Knowledge Items</CardDescription>
                  <CardTitle style={{ color: '#22D3EE', fontSize: '2rem' }}>{stats.knowledge_base_items}</CardTitle>
                </CardHeader>
              </Card>
            </div>
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-8">
            <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
              <CardHeader>
                <CardTitle style={{ color: '#E6EDF3' }}>{t('newDiagnosis')}</CardTitle>
                <CardDescription style={{ color: '#94A3B8' }}>
                  Starten Sie eine neue Fahrzeugdiagnose
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={() => navigate('/diagnosis/new')}
                  data-testid="start-diagnosis-button"
                  className="w-full"
                  style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('startDiagnosis')}
                </Button>
              </CardContent>
            </Card>

            <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
              <CardHeader>
                <CardTitle style={{ color: '#E6EDF3' }}>{t('myVehicles')}</CardTitle>
                <CardDescription style={{ color: '#94A3B8' }}>
                  {vehicles.length} Fahrzeuge registriert
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={() => navigate('/vehicles')}
                  data-testid="manage-vehicles-button"
                  variant="outline"
                  className="w-full"
                  style={{ borderColor: '#1F2937', color: '#E6EDF3' }}
                >
                  {t('manage')}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Recent Sessions */}
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardHeader>
              <CardTitle style={{ color: '#E6EDF3' }}>{t('history')}</CardTitle>
              <CardDescription style={{ color: '#94A3B8' }}>
                Ihre letzten Diagnose-Sessions
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p style={{ color: '#94A3B8' }}>Laden...</p>
              ) : sessions.length === 0 ? (
                <p style={{ color: '#94A3B8' }}>Noch keine Sessions vorhanden.</p>
              ) : (
                <div className="space-y-3">
                  {sessions.slice(0, 5).map((session) => (
                    <div
                      key={session.id}
                      className="p-4 rounded-lg flex justify-between items-center cursor-pointer hover:bg-opacity-80"
                      style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', border: '1px solid' }}
                      onClick={() => navigate(`/history`)}
                    >
                      <div>
                        <p style={{ color: '#E6EDF3' }}>{session.symptom_text?.substring(0, 60)}...</p>
                        <p className="text-sm" style={{ color: '#94A3B8' }}>
                          {new Date(session.created_at).toLocaleDateString('de-DE')}
                        </p>
                      </div>
                      <span
                        className="px-3 py-1 rounded-full text-sm"
                        style={{
                          backgroundColor: session.status === 'done' ? '#22C55E20' : '#F59E0B20',
                          color: session.status === 'done' ? '#22C55E' : '#F59E0B'
                        }}
                      >
                        {session.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Referral Widget - Only for non-admin view */}
        {!isAdminView && (
          <div className="mb-8">
            <ReferralWidget />
          </div>
        )}
    </div>
  );
};
